import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';

interface ChatSidebarCollapseContextValue {
  /** Whether the chat sidebar is collapsed */
  isCollapsed: boolean;
  /** Set the collapsed state */
  setIsCollapsed: (collapsed: boolean) => void;
  /** Expand the sidebar (set collapsed to false) */
  expandSidebar: () => void;
  /** Collapse the sidebar (set collapsed to true) */
  collapseSidebar: () => void;
  /** Whether auto-collapse is active (e.g., live campaign thread) */
  autoCollapseActive: boolean;
  /** Set whether auto-collapse should be active */
  setAutoCollapseActive: (active: boolean) => void;
}

const ChatSidebarCollapseContext = createContext<
  ChatSidebarCollapseContextValue | undefined
>(undefined);

interface ChatSidebarCollapseProviderProps {
  threadId: string | null;
  children: ReactNode;
}

export const ChatSidebarCollapseProvider: React.FC<
  ChatSidebarCollapseProviderProps
> = ({ threadId, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [autoCollapseActive, setAutoCollapseActive] = useState(false);

  // Reset collapsed state when threadId changes (including when navigating away)
  useEffect(() => {
    setIsCollapsed(false);
    setAutoCollapseActive(false);
  }, [threadId]);

  const expandSidebar = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const collapseSidebar = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  return (
    <ChatSidebarCollapseContext.Provider
      value={{
        isCollapsed,
        setIsCollapsed,
        expandSidebar,
        collapseSidebar,
        autoCollapseActive,
        setAutoCollapseActive,
      }}
    >
      {children}
    </ChatSidebarCollapseContext.Provider>
  );
};

export const useChatSidebarCollapse = (): ChatSidebarCollapseContextValue => {
  const context = useContext(ChatSidebarCollapseContext);
  if (!context) {
    throw new Error(
      'useChatSidebarCollapse must be used within a ChatSidebarCollapseProvider',
    );
  }
  return context;
};

/**
 * Hook that returns the context value or undefined if not in provider
 * Useful for components that may or may not be within the provider
 */
export const useChatSidebarCollapseOptional = ():
  | ChatSidebarCollapseContextValue
  | undefined => {
  return useContext(ChatSidebarCollapseContext);
};
